import { useState } from 'react';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Legend
} from 'recharts';
import { getAIWeeklySummary } from '../api';

const MACROS = [
  { key: 'protein', label: 'Protein', color: '#60a5fa' },
  { key: 'carbs',   label: 'Carbs',   color: '#fbbf24' },
  { key: 'fat',     label: 'Fat',     color: '#34d399' },
];

const glass = {
  background:           'rgba(255,255,255,0.07)',
  backdropFilter:       'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border:               '1px solid rgba(255,255,255,0.12)',
  borderRadius:         16,
};

function statusColor(value, goal) {
  const pct = (value / goal) * 100;
  if (pct >= 90 && pct <= 110) return '#34d399';
  if (pct >= 70 && pct <   90) return '#fbbf24';
  return '#ff6b6b';
}

function statusLabel(value, goal, unit) {
  const diff = Math.round(goal - value);
  const pct  = (value / goal) * 100;
  if (pct >= 90 && pct <= 110) return { text: 'On target ✅', color: '#34d399' };
  if (diff > 0) return { text: `${diff}${unit} below goal`, color: '#fbbf24' };
  return { text: `${Math.abs(diff)}${unit} over goal`,  color: '#ff6b6b' };
}

const tooltipStyle = {
  background:   'rgba(15,20,40,0.95)',
  border:       '1px solid rgba(255,255,255,0.15)',
  borderRadius: 10,
  color:        '#fff',
  fontSize:     13,
};

const sectionTitle = (title) => (
  <h4 style={{
    margin: '24px 0 12px',
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: 600,
  }}>{title}</h4>
);

export default function Stats({ weekData, goals }) {
  const [aiSummary,  setAiSummary]  = useState('');
  const [aiLoading,  setAiLoading]  = useState(false);
  const [aiError,    setAiError]    = useState('');
  const [aiRequested, setAiRequested] = useState(false);

  async function handleGetInsights() {
    setAiLoading(true);
    setAiError('');
    setAiRequested(true);
    try {
      const summary = await getAIWeeklySummary(weekData, goals);
      setAiSummary(summary);
    } catch (e) {
      console.error('AI summary error', e);
      setAiError('Could not get AI insights right now. Please try again.');
    }
    setAiLoading(false);
  }

  if (!weekData || weekData.length === 0) {
    return (
      <div style={{ textAlign: 'center', marginTop: 60 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 15 }}>
          No data yet — log some meals first!
        </p>
      </div>
    );
  }

  const days = weekData.length;
  const avg  = weekData.reduce((acc, d) => ({
    calories: acc.calories + d.calories,
    protein:  acc.protein  + d.protein,
    carbs:    acc.carbs    + d.carbs,
    fat:      acc.fat      + d.fat,
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
  avg.calories = Math.round(avg.calories / days);
  avg.protein  = Math.round(avg.protein  / days);
  avg.carbs    = Math.round(avg.carbs    / days);
  avg.fat      = Math.round(avg.fat      / days);

  const best  = weekData.reduce((a, b) => a.calories > b.calories ? a : b);
  const worst = weekData.reduce((a, b) => a.calories < b.calories ? a : b);

  return (
    <div>
      <h2 style={{
        margin: '0 0 20px', fontSize: 22, fontWeight: 700,
        background: 'linear-gradient(135deg, #fff 0%, #a0c4ff 100%)',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
      }}>
        Weekly Stats
      </h2>

      {/* ── AI Insights button ── */}
      {!aiRequested ? (
        <button onClick={handleGetInsights} style={{
          width: '100%', padding: 16, borderRadius: 14, border: 'none',
          background: 'linear-gradient(135deg, #a855f7, #6366f1)',
          color: '#fff', fontSize: 15, fontWeight: 700,
          cursor: 'pointer', marginBottom: 20,
          boxShadow: '0 4px 20px rgba(168,85,247,0.35)',
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 8,
        }}>
          ✨ Get AI Insights
        </button>
      ) : (
        <div style={{
          ...glass,
          background: 'rgba(168,85,247,0.1)',
          borderColor: 'rgba(168,85,247,0.3)',
          padding: 18, marginBottom: 20,
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginBottom: aiSummary ? 12 : 0
          }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#c084fc' }}>
              ✨ AI Weekly Insights
            </span>
            <button onClick={handleGetInsights} disabled={aiLoading}
              style={{
                fontSize: 12, color: 'rgba(168,85,247,0.8)',
                background: 'none', border: '1px solid rgba(168,85,247,0.3)',
                borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
              }}>
              {aiLoading ? '...' : '↻ Refresh'}
            </button>
          </div>

          {aiLoading && (
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)',
              fontStyle: 'italic' }}>
              🤖 Analysing your week...
            </div>
          )}

          {aiError && !aiLoading && (
            <div style={{ fontSize: 13, color: '#ff6b6b' }}>
              ⚠️ {aiError}
            </div>
          )}

          {aiSummary && !aiLoading && (
            <p style={{
              fontSize: 14, color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.7, margin: 0,
            }}>
              {aiSummary}
            </p>
          )}
        </div>
      )}

      {/* ── Best / Worst ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: 10, marginBottom: 4 }}>
        <div style={{ ...glass, padding: 16, textAlign: 'center',
          background: 'rgba(52,211,153,0.1)',
          borderColor: 'rgba(52,211,153,0.2)' }}>
          <div style={{ fontSize: 11, color: '#34d399', fontWeight: 700,
            marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            🏆 Best Day
          </div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{best.day}</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)',
            marginTop: 2 }}>
            {best.calories} kcal
          </div>
        </div>
        <div style={{ ...glass, padding: 16, textAlign: 'center',
          background: 'rgba(255,107,107,0.1)',
          borderColor: 'rgba(255,107,107,0.2)' }}>
          <div style={{ fontSize: 11, color: '#ff6b6b', fontWeight: 700,
            marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            📉 Lowest Day
          </div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{worst.day}</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)',
            marginTop: 2 }}>
            {worst.calories} kcal
          </div>
        </div>
      </div>

      {/* ── Daily average vs goal ── */}
      {sectionTitle('Daily Average vs Goal')}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: 10, marginBottom: 4 }}>
        {[
          { label: 'Calories', key: 'calories',
            goal: goals.dailyCalories, unit: ' kcal' },
          { label: 'Protein',  key: 'protein',
            goal: goals.dailyProtein,  unit: 'g' },
          { label: 'Carbs',    key: 'carbs',
            goal: goals.dailyCarbs,    unit: 'g' },
          { label: 'Fat',      key: 'fat',
            goal: goals.dailyFat,      unit: 'g' },
        ].map(({ label, key, goal, unit }) => {
          const value = avg[key];
          const s     = statusLabel(value, goal, unit);
          return (
            <div key={key} style={{ ...glass, padding: 14 }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)',
                marginBottom: 6, textTransform: 'uppercase',
                letterSpacing: 0.5 }}>
                {label}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700,
                color: statusColor(value, goal) }}>
                {value}{unit}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)',
                marginTop: 2 }}>
                goal: {goal}{unit}
              </div>
              <div style={{ fontSize: 11, color: s.color,
                marginTop: 6, fontWeight: 600 }}>
                {s.text}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Calorie bar chart ── */}
      {sectionTitle('Daily Calories')}
      <div style={{ ...glass, padding: '16px 4px 8px' }}>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={weekData}
            margin={{ top: 0, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.07)" vertical={false} />
            <XAxis dataKey="day"
              tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
              axisLine={false} tickLine={false} />
            <YAxis
              tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
              axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle}
              formatter={(v) => [`${v} kcal`, 'Calories']}
              cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
            <ReferenceLine y={goals.dailyCalories}
              stroke="#ff6b6b" strokeDasharray="4 4"
              label={{ value: 'Goal', position: 'insideTopRight',
                fontSize: 10, fill: '#ff6b6b' }} />
            <defs>
              <linearGradient id="calGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#5282ff" stopOpacity={1} />
                <stop offset="100%" stopColor="#a855f7" stopOpacity={0.8} />
              </linearGradient>
            </defs>
            <Bar dataKey="calories" fill="url(#calGradient)"
              radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ── Macro line chart ── */}
      {sectionTitle('Macros Over 7 Days')}
      <div style={{ ...glass, padding: '16px 4px 8px' }}>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={weekData}
            margin={{ top: 0, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.07)" vertical={false} />
            <XAxis dataKey="day"
              tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
              axisLine={false} tickLine={false} />
            <YAxis
              tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
              axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle}
              formatter={(v, name) => [`${v}g`, name]}
              cursor={{ stroke: 'rgba(255,255,255,0.1)' }} />
            <Legend wrapperStyle={{
              fontSize: 12, color: 'rgba(255,255,255,0.6)',
              paddingTop: 8
            }} />
            {MACROS.map(m => (
              <Line key={m.key} type="monotone" dataKey={m.key}
                stroke={m.color} strokeWidth={2.5}
                dot={{ r: 3, fill: m.color, strokeWidth: 0 }}
                activeDot={{ r: 5 }} name={m.label} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ── Text suggestions ── */}
      {sectionTitle('Suggestions')}
      {[
        { label: 'Calories', key: 'calories',
          goal: goals.dailyCalories, unit: ' kcal',
          tips: {
            low:  "You're consistently under your calorie goal. Consider adding a healthy snack or slightly larger portions.",
            high: "You're exceeding your calorie goal on average. Try reducing portion sizes.",
            ok:   "Your calorie intake is well balanced. Keep it up!"
          }
        },
        { label: 'Protein', key: 'protein',
          goal: goals.dailyProtein, unit: 'g',
          tips: {
            low:  "Protein is low. Add eggs, chicken, dal, paneer, or Greek yogurt to your meals.",
            high: "Protein is above goal — generally fine for most people.",
            ok:   "Protein intake is on track. Great for muscle maintenance!"
          }
        },
        { label: 'Carbs', key: 'carbs',
          goal: goals.dailyCarbs, unit: 'g',
          tips: {
            low:  "Carbs are low. Add whole grains, roti, or fruits for sustained energy.",
            high: "Carbs are above goal. Consider swapping refined carbs for vegetables.",
            ok:   "Carb intake looks balanced!"
          }
        },
        { label: 'Fat', key: 'fat',
          goal: goals.dailyFat, unit: 'g',
          tips: {
            low:  "Fat is low. Add avocado, nuts, or a little ghee for healthy fats.",
            high: "Fat is above goal. Watch fried foods and heavy sauces.",
            ok:   "Fat intake looks good!"
          }
        },
      ].map(({ label, key, goal, unit, tips }) => {
        const value = avg[key];
        const pct   = (value / goal) * 100;
        const type  = pct >= 90 && pct <= 110
          ? 'ok' : pct > 110 ? 'high' : 'low';
        const color = type === 'ok'   ? '#34d399'
          : type === 'high' ? '#ff6b6b' : '#fbbf24';
        return (
          <div key={key} style={{
            ...glass, padding: 14, marginBottom: 10,
            borderLeft: `4px solid ${color}`,
            background: `rgba(${
              type === 'ok'   ? '52,211,153' :
              type === 'high' ? '255,107,107' :
              '251,191,36'},0.08)`
          }}>
            <div style={{ fontWeight: 700, fontSize: 14,
              color, marginBottom: 6 }}>
              {label}: avg {value}{unit} / goal {goal}{unit}
            </div>
            <div style={{ fontSize: 13,
              color: 'rgba(255,255,255,0.6)' }}>
              {tips[type]}
            </div>
          </div>
        );
      })}
    </div>
  );
}